import classNames from "classnames";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { ReactComponent as AntennaIcon } from "assets/images/common/antenna.svg";
import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";
import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import PageTemplate from "modules/common/PageTemplate";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import Popover from "components/Popover";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";
import TableRowActions from "components/Table/TableRowActions";

import { CompaniesApi, PortfoliosApi } from "services/http";

import { BaseOption, ModalType, UserRole } from "entities/common";
import {
  GATEWAY_MODELS,
  GATEWAY_STATUS_LABELS,
  GatewayItem,
  GatewayStatus,
} from "entities/gateways";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  createGatewayRequest,
  getGatewayListRequest,
  getGatewayStatisticsRequest,
  removeGatewayRequest,
  resetSuccessfullyAddedGateway,
  resetSuccessfullyRemovedGateway,
  resetSuccessfullyUpdatedGateway,
  updateGatewayRequest,
} from "store/slices/gatewaySlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";

import { GatewayFormValues } from "widgets/Modal/CreateGateway/Content";

import styles from "./style.module.scss";

const PAGE_SIZE = 12;
const RELATIVE_TIME_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// FR 6.2.3.9: "DD.MM.YYYY HH:MM" in user's local timezone.
const formatAbsoluteDateTime = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

// FR 6.2.3.9: "Just now" / "N min ago" / "N h ago" for last_seen < 24h.
const formatRelativeTime = (diffMs: number) => {
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ago`;
};

const toCreateBody = (values: GatewayFormValues) => ({
  gateway_name: values.name,
  company_id: values.companyId ? Number(values.companyId) : null,
  portfolio_id: values.portfolioId ? Number(values.portfolioId) : null,
  gateway_model: values.gatewayModel,
  frequency_band: values.frequencyBand,
  gateway_eui: values.gatewayEui,
});

const toUpdateBody = (values: GatewayFormValues) => ({
  gateway_name: values.name,
  company_id: values.companyId ? Number(values.companyId) : null,
  portfolio_id: values.portfolioId ? Number(values.portfolioId) : null,
  frequency_band: values.frequencyBand,
});

const GatewaysListPage = () => {
  const dispatch = useAppDispatch();
  const callbackKey = useId();
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  const [companyId, setCompanyId] = useState<string>("");
  const [companyLabel, setCompanyLabel] = useState<string>("All companies");
  const [portfolioId, setPortfolioId] = useState<string>("");
  const [portfolioLabel, setPortfolioLabel] = useState<string>("All portfolios");
  const [statusValue, setStatusValue] = useState<string>("");
  const [modelValue, setModelValue] = useState<string>("");

  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;
  const {
    items,
    meta,
    isLoading,
    statistics,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
  } = useAppSelector((state: RootStore) => state.gateways);

  const fetchList = useCallback(
    (silent = false) => {
      const query = QueryHelper.getQueryString({
        [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
        [QUERY_PARAMS.COMPANY_ID]: companyId ? Number(companyId) : undefined,
        [QUERY_PARAMS.PORTFOLIO_ID]: portfolioId ? Number(portfolioId) : undefined,
        [QUERY_PARAMS.STATUS]: statusValue || undefined,
        [QUERY_PARAMS.GATEWAY_MODEL]: modelValue || undefined,
        [QUERY_PARAMS.PAGE]: page,
        [QUERY_PARAMS.PAGE_SIZE]: PAGE_SIZE,
      });
      dispatch(getGatewayListRequest(query, silent ? { silent: true } : undefined));
    },
    [companyId, debouncedSearch, dispatch, modelValue, page, portfolioId, statusValue],
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    dispatch(getGatewayStatisticsRequest());
  }, [dispatch]);

  const reload = useCallback(() => {
    fetchList(true);
    dispatch(getGatewayStatisticsRequest({ silent: true }));
  }, [fetchList, dispatch]);

  useRealtime(["gateways"], reload);

  useEffect(() => {
    if (successfullyAddedId || successfullyUpdatedId || successfullyRemovedId) {
      if (successfullyUpdatedId || successfullyRemovedId) {
        dispatch(hideModal());
      }
      fetchList();
      dispatch(getGatewayStatisticsRequest());
      if (successfullyAddedId) dispatch(resetSuccessfullyAddedGateway());
      if (successfullyUpdatedId) dispatch(resetSuccessfullyUpdatedGateway());
      if (successfullyRemovedId) dispatch(resetSuccessfullyRemovedGateway());
    }
  }, [dispatch, fetchList, successfullyAddedId, successfullyRemovedId, successfullyUpdatedId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleViewGateway = useCallback(
    (id: number) => {
      const gateway = items.find((g) => g.id === id);
      if (!gateway) return;
      dispatch(
        showModal({
          modalType: ModalType.GATEWAY_CONFIG_INSTRUCTIONS,
          modalProps: {
            gatewayName: gateway.name,
            gatewayEui: gateway.gateway_eui,
          },
        }),
      );
    },
    [dispatch, items],
  );

  const handleSaveGateway = useCallback(
    (values?: unknown) => {
      const data = values as GatewayFormValues;
      dispatch(createGatewayRequest(toCreateBody(data)));
    },
    [dispatch],
  );

  const handleUpdateGateway = useCallback(
    (id: number) => (values?: unknown) => {
      const data = values as GatewayFormValues;
      dispatch(updateGatewayRequest({ id, body: toUpdateBody(data) }));
    },
    [dispatch],
  );

  const handleAddGateway = useCallback(() => {
    if (!isAdmin) return;
    setModalCallbacks(callbackKey, { onSave: handleSaveGateway });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_GATEWAY,
        modalProps: { mode: "create", callbackKey },
      }),
    );
  }, [callbackKey, dispatch, handleSaveGateway, isAdmin]);

  const handleEditGateway = useCallback(
    (id: number) => {
      if (!isAdmin) return;
      const gateway = items.find((g) => g.id === id);
      if (!gateway) return;

      setModalCallbacks(callbackKey, { onSave: handleUpdateGateway(id) });

      const initialData: GatewayFormValues = {
        name: gateway.name,
        companyId: gateway.company_id ? String(gateway.company_id) : "",
        companyLabel: gateway.company?.name ?? "",
        portfolioId: gateway.portfolio_id ? String(gateway.portfolio_id) : "",
        portfolioLabel: gateway.portfolio?.name ?? "",
        gatewayModel: gateway.gateway_model,
        frequencyBand: gateway.frequency_band,
        gatewayEui: gateway.gateway_eui,
      };

      dispatch(
        showModal({
          modalType: ModalType.CREATE_GATEWAY,
          modalProps: { mode: "edit", id, callbackKey, initialData },
        }),
      );
    },
    [callbackKey, dispatch, handleUpdateGateway, isAdmin, items],
  );

  const handleConfirmRemoveGateway = useCallback(
    (id: number) => () => {
      dispatch(removeGatewayRequest(id));
    },
    [dispatch],
  );

  const handleRemoveGateway = useCallback(
    (gateway: GatewayItem) => (id: number) => {
      if (!isAdmin) return;
      setModalCallbacks(callbackKey, { onClick: handleConfirmRemoveGateway(id) });
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_GATEWAY,
          // FR 6.2.4: modal itself fetches sensor count from the backend on
          // open; no need to pre-compute it here.
          modalProps: {
            id,
            name: gateway.name,
            callbackKey,
          },
        }),
      );
    },
    [callbackKey, dispatch, handleConfirmRemoveGateway, isAdmin],
  );

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const handleResetFilters = useCallback(() => {
    // FR 6.2.3.8: reset all four filters to defaults; preserve text search.
    setCompanyId("");
    setCompanyLabel("All companies");
    setPortfolioId("");
    setPortfolioLabel("All portfolios");
    setStatusValue("");
    setModelValue("");
    setPage(1);
  }, []);

  const searchCompanies = useCallback(async (searchTerm: string, pageNum: number) => {
    const SELECT_PAGE_SIZE = 50;
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
          [QUERY_PARAMS.PAGE]: pageNum,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const fetched: BaseOption[] = (data?.items ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      }));

      const showAllSentinel = pageNum === 1 && !searchTerm.trim();
      const options: BaseOption[] = showAllSentinel
        ? [{ value: "", label: "All companies" }, ...fetched]
        : fetched;

      const hasMore = (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE;
      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const searchPortfolios = useCallback(
    async (searchTerm: string, pageNum: number) => {
      const SELECT_PAGE_SIZE = 50;
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
            [QUERY_PARAMS.PAGE]: pageNum,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            // FR 6.2.3.5: portfolios list is scoped to the selected company.
            ...(companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
        );
        const fetched: BaseOption[] = (data?.items ?? []).map((p) => ({
          value: String(p.id),
          label: p.name,
          companyId: p.company_id,
          companyName: p.company_name,
        }));

        const showAllSentinel = pageNum === 1 && !searchTerm.trim();
        const options: BaseOption[] = showAllSentinel
          ? [{ value: "", label: "All portfolios" }, ...fetched]
          : fetched;

        const hasMore = (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE;
        return { options, hasMore };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId],
  );

  const handleCompanyFilterChange = useCallback(
    (value: string, label: string) => {
      setCompanyId(value);
      setCompanyLabel(value ? label : "All companies");
      // FR 6.2.3.5: changing Company resets Portfolio to its default.
      if (portfolioId) {
        setPortfolioId("");
        setPortfolioLabel("All portfolios");
      }
      setPage(1);
    },
    [portfolioId],
  );

  const handlePortfolioFilterChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setPortfolioId(value);
      setPortfolioLabel(value ? label : "All portfolios");
      // FR 6.2.3.5: when Company is empty and a Portfolio is picked, auto-fill
      // Company with that portfolio's company.
      if (value && !companyId && option?.companyId) {
        setCompanyId(String(option.companyId));
        setCompanyLabel((option.companyName as string) ?? "");
      }
      setPage(1);
    },
    [companyId],
  );

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusValue(value);
    setPage(1);
  }, []);

  const handleModelFilterChange = useCallback((value: string) => {
    setModelValue(value);
    setPage(1);
  }, []);

  const statusOptions = useMemo<BaseOption[]>(
    () => [
      { value: "", label: "All statuses" },
      { value: "online", label: GATEWAY_STATUS_LABELS.online },
      { value: "offline", label: GATEWAY_STATUS_LABELS.offline },
      { value: "never_seen", label: GATEWAY_STATUS_LABELS.never_seen },
    ],
    [],
  );

  const modelOptions = useMemo<BaseOption[]>(
    () => [
      { value: "", label: "All models" },
      ...GATEWAY_MODELS.map((m) => ({ value: m, label: m })),
    ],
    [],
  );

  const totalPages = useMemo(() => Math.max(1, meta.total_pages), [meta.total_pages]);

  const gatewaysInfo = useMemo(
    () => [
      {
        key: "total",
        title: "Total gateways",
        value: statistics.total.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--gray"])}>
            <AntennaIcon />
          </span>
        ),
      },
      {
        key: "online",
        title: "Online",
        value: statistics.online.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--green"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
      {
        key: "offline",
        title: "Offline",
        value: statistics.offline.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--red"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
      {
        key: "never_seen",
        title: "Never seen",
        value: statistics.never_seen.toString(),
        icon: (
          <span className={classNames(styles.statIcon, styles["statIcon--orange"])}>
            <span className={styles.statDot} />
          </span>
        ),
      },
    ],
    [statistics],
  );

  const tableHeaders = useMemo(
    () => [
      { label: "Gateway name", key: "name" },
      { label: "Gateway model", key: "model" },
      { label: "Freq. band", key: "freqBand" },
      { label: "Company", key: "company" },
      { label: "Portfolio", key: "portfolio" },
      { label: "Status", key: "status" },
      { label: "Last seen", key: "lastSeen" },
      { label: "Actions", key: "actions" },
    ],
    [],
  );

  const tableData = useMemo(() => {
    const statusClassByValue: Record<GatewayStatus, string> = {
      online: styles.online,
      offline: styles.offline,
      never_seen: styles["never-seen"],
    };

    const now = Date.now();

    return items.map((gateway) => {
      const status: GatewayStatus = gateway.status ?? "never_seen";

      // FR 6.2.3.9: "—" for never_seen; relative time + tooltip for <24h;
      // absolute date with no tooltip for ≥24h.
      const lastSeenDate = gateway.last_seen ? new Date(gateway.last_seen) : null;
      const diffMs = lastSeenDate ? now - lastSeenDate.getTime() : 0;
      const absolute = lastSeenDate ? formatAbsoluteDateTime(lastSeenDate) : "";
      const isRelative = lastSeenDate !== null && diffMs < RELATIVE_TIME_THRESHOLD_MS;

      const lastSeenContent = !lastSeenDate ? (
        <span className={styles.lastSeenText}>—</span>
      ) : isRelative ? (
        <>
          <span className={styles.lastSeenText}>{formatRelativeTime(diffMs)}</span>
          <Popover
            id={`gateway-last-seen-${gateway.id}`}
            variant="hover"
            positionY="middle"
            positionX="content-end"
            content={absolute}
            popoverClassName={styles.lastSeenPopover}
          >
            <span className={styles.lastSeenInfo}>
              <InfoIcon />
            </span>
          </Popover>
        </>
      ) : (
        <span className={styles.lastSeenText}>{absolute}</span>
      );

      return {
        id: gateway.id,
        name: (
          <span className={styles.gatewayName}>
            <span className={styles.gatewayNameTitle}>{gateway.name}</span>
            <span className={styles.gatewayNameSerial}>{gateway.gateway_eui}</span>
          </span>
        ),
        model: <span className={styles.modelName}>{gateway.gateway_model}</span>,
        freqBand: <span className={styles.freqBand}>{gateway.frequency_band}</span>,
        company: <span className={styles.company}>{gateway.company?.name ?? "—"}</span>,
        portfolio: <span className={styles.portfolio}>{gateway.portfolio?.name ?? "—"}</span>,
        status: (
          <span className={classNames(styles.status, statusClassByValue[status])}>
            {GATEWAY_STATUS_LABELS[status]}
          </span>
        ),
        lastSeen: <span className={styles.lastSeen}>{lastSeenContent}</span>,
        actions: (
          <TableRowActions
            id={gateway.id}
            flags={{ view: true, edit: isAdmin, remove: isAdmin }}
            onView={handleViewGateway}
            onEdit={handleEditGateway}
            onRemove={handleRemoveGateway(gateway)}
          />
        ),
      };
    });
  }, [items, isAdmin, handleViewGateway, handleEditGateway, handleRemoveGateway]);

  return (
    <PageTemplate
      title="Gateway management"
      description="Monitor and manage all IoT gateways across your system"
      isBackButton
      backButtonLink={PAGES.SETTINGS}
      actions={
        isAdmin && (
          <Button variant="primary" className={styles["header-create"]} onClick={handleAddGateway}>
            <span className={styles["header-create-text"]}>Add gateway</span>
            <span className={styles["header-create-icon"]}>
              <PlusIcon />
            </span>
          </Button>
        )
      }
      className={styles.list}
    >
      <Card>
        {!!statistics.total && (
          <div className={styles.info}>
            {gatewaysInfo.map((item) => (
              <div className={styles.infoItem} key={item.key}>
                <div className={styles.infoItemContent}>
                  <p className={styles.infoItemValue}>{item.value}</p>
                  <p className={styles.infoItemTitle}>{item.title}</p>
                </div>
                {item.icon}
              </div>
            ))}
          </div>
        )}

        <div className={styles.search}>
          <SearchInput
            name="search"
            value={search}
            onChange={handleSearchChange}
            className={styles["search-input"]}
            placeholder="Search gateways..."
          />

          <Button variant="link" className={styles["filters-button"]} onClick={handleToggleFilters}>
            {showFilters ? "Close" : "Open"} filters
          </Button>
        </div>

        {showFilters && !!statistics.total && (
          <div className={styles.filters}>
            <AsyncSearchableSelect
              variant="secondary"
              value={companyId}
              selectedLabel={companyLabel}
              onSearch={searchCompanies}
              onChange={handleCompanyFilterChange}
              placeholder="All companies"
              searchPlaceholder="Search"
              className={styles.select}
            />

            <AsyncSearchableSelect
              variant="secondary"
              value={portfolioId}
              selectedLabel={portfolioLabel}
              onSearch={searchPortfolios}
              onChange={handlePortfolioFilterChange}
              placeholder="All portfolios"
              searchPlaceholder="Search"
              className={styles.select}
            />

            <Select
              variant="secondary"
              options={statusOptions}
              value={statusValue}
              onChange={handleStatusFilterChange}
              placeholder="All statuses"
              className={styles.select}
            />

            <Select
              variant="secondary"
              options={modelOptions}
              value={modelValue}
              onChange={handleModelFilterChange}
              placeholder="All models"
              className={styles.select}
            />

            <Button variant="secondary" className={styles.reload} onClick={handleResetFilters}>
              <span className={styles["reload-icon"]}>
                <ReloadIcon />
              </span>
              <span className={styles["reload-text"]}>Clear filters</span>
            </Button>
          </div>
        )}

        {!isLoading && tableData.length === 0 && (
          <div className={styles["not-found"]}>
            <NotFoundIcon />
            <p>{!statistics.total ? "No gateways yet" : "No gateways found"}</p>
            {!statistics.total && isAdmin && (
              <Button variant="secondary" onClick={handleAddGateway} className={styles["not-found-button"]}>
                Add first gateway
              </Button>
            )}
          </div>
        )}

        {tableData.length > 0 && (
          <div className={styles["table-scroll"]}>
            <Table
              headers={tableHeaders}
              data={tableData}
              pagination={{
                total: totalPages,
                page,
                onPageChange: handlePageChange,
              }}
              className={styles.table}
            />
          </div>
        )}
      </Card>
    </PageTemplate>
  );
};

export default GatewaysListPage;
